import { useEffect, useRef } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { getApiClient } from '@signalvote/api-client';
import {
  CalendarConnectionStatus,
  CalendarSyncState,
  type CalendarProvider,
  type CalendarConnectionDto,
  type UpdateCalendarPreferenceRequest,
  type UpdateCalendarConnectionRequest,
  type CampaignCalendarVisibility,
  type CalendarEventsResponse,
  type KeyDatesResponse,
} from '@signalvote/shared';
import { queryKeys } from '@/lib/queryKeys';
import { defineQuery, requireSelectedCampaign, selectedCampaignKey } from '@/lib/data';

const api = getApiClient();

// ============================================================================
// Connection Management
// ============================================================================

const calendarConnections = defineQuery({
  key: () => ['calendar', 'connections'],
  reads: 'GET /api/calendar/connections',
  staleTime: 60 * 1000,
  fetch: (_: undefined, { campaignId }): Promise<CalendarConnectionDto[]> => {
    requireSelectedCampaign(campaignId);
    return api.calendar.getConnections();
  },
});

/**
 * The current user's calendar connections. A sync runs in the background, so no write set names
 * its end: while any connection syncs this polls every 2s, then refreshes the calendar reads.
 */
export function useCalendarConnections() {
  const queryClient = useQueryClient();
  const wasSyncingRef = useRef(false);
  const read = calendarConnections(undefined);
  const isSyncing = read.status === 'ready' && read.data.some((c) => c.syncState === CalendarSyncState.Syncing);

  useEffect(() => {
    if (!isSyncing) {
      if (wasSyncingRef.current) {
        wasSyncingRef.current = false;
        queryClient.invalidateQueries({ queryKey: selectedCampaignKey(['calendar']) });
      }
      return;
    }

    wasSyncingRef.current = true;
    const interval = setInterval(() => {
      queryClient.invalidateQueries({ queryKey: selectedCampaignKey(['calendar', 'connections']) });
    }, 2000);

    return () => clearInterval(interval);
  }, [isSyncing, queryClient]);

  return { read, isSyncing };
}

/** Whether any calendar connection is non-Active (expired, revoked, or errored). */
export function useHasUnhealthyCalendarConnections() {
  const { read } = useCalendarConnections();
  return read.status === 'ready' && read.data.some((c) => c.status !== CalendarConnectionStatus.Active);
}

/**
 * Start OAuth flow for connecting a calendar provider.
 * Returns an authorization URL to redirect the user to.
 */
export function useStartCalendarConnection() {
  return useMutation({
    mutationFn: ({
      provider,
      redirectUri,
    }: {
      provider: CalendarProvider;
      redirectUri: string;
    }) => api.calendar.startConnection(provider.toLowerCase(), { redirectUri }),
  });
}

/**
 * Disconnect a calendar connection
 */
export function useDisconnectCalendar() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (connectionId: string) => api.calendar.disconnectAccount(connectionId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.calendar.all });
    },
  });
}

/**
 * Update a calendar connection's display name and/or color
 */
export function useUpdateCalendarConnection() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      connectionId,
      request,
    }: {
      connectionId: string;
      request: Partial<UpdateCalendarConnectionRequest>;
    }) => api.calendar.updateConnection(connectionId, { displayName: null, color: null, ...request }),
    onSuccess: () => {
      // Invalidate all calendar queries (connections, events, etc.)
      queryClient.invalidateQueries({ queryKey: queryKeys.calendar.all });
    },
  });
}

// ============================================================================
// Calendar Preferences
// ============================================================================

/** A connection's calendars and their sync preferences; a preference or connection write refreshes it. */
export const useCalendarsForConnection = defineQuery({
  key: (connectionId: string) => ['calendar', 'calendars', connectionId],
  reads: 'GET /api/calendar/connections/{id}/calendars',
  staleTime: 60 * 1000,
  fetch: (connectionId, { campaignId }) => {
    requireSelectedCampaign(campaignId);
    return api.calendar.getCalendars(connectionId);
  },
});

/**
 * Refresh the list of calendars from the provider.
 * Useful when user creates a new calendar in Google/Microsoft.
 */
export function useRefreshCalendarList() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (connectionId: string) => api.calendar.refreshCalendars(connectionId),
    onSuccess: (_, connectionId) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.calendar.calendars(connectionId),
      });
    },
  });
}

/**
 * Update sync preferences for a specific calendar
 */
export function useUpdateCalendarPreference() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      connectionId,
      calendarId,
      request,
    }: {
      connectionId: string;
      calendarId: string;
      request: Partial<UpdateCalendarPreferenceRequest>;
    }) =>
      api.calendar.updateCalendarPreference(connectionId, calendarId, {
        readEventsEnabled: null,
        writeShiftsEnabled: null,
        isPrimaryWriteCalendar: null,
        color: null,
        ...request,
      }),
    onSuccess: (_, { connectionId }) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.calendar.calendars(connectionId),
      });
      // Also invalidate events so color changes are reflected in the calendar view
      queryClient.invalidateQueries({ queryKey: queryKeys.calendar.all });
    },
  });
}

// ============================================================================
// Events
// ============================================================================

/** The UTC instants a calendar events read covers. */
export interface CalendarEventsRange {
  start: string;
  end: string;
}

/**
 * Personal and campaign calendar events within a range. It declares its GET's reads, so a write
 * naming a connection, a calendar preference, a cached event or a visibility refreshes it.
 */
export const useCalendarEvents = defineQuery({
  key: (range: CalendarEventsRange) => ['calendar', 'events', range.start, range.end],
  reads: 'GET /api/calendar/events',
  staleTime: 30 * 1000,
  fetch: (range, { campaignId }): Promise<CalendarEventsResponse> => {
    requireSelectedCampaign(campaignId);
    return api.calendar.getEvents(range);
  },
});

/** Election stages and filing deadlines; a write to finance settings or a deadline refreshes it. */
export const useKeyDates = defineQuery({
  key: () => ['calendar', 'key-dates'],
  reads: 'GET /api/calendar/key-dates',
  fetch: (_: undefined, { campaignId }): Promise<KeyDatesResponse> => {
    requireSelectedCampaign(campaignId);
    return api.keyDates.getKeyDates();
  },
});

/**
 * Manually trigger a calendar sync.
 * Syncs events from connected calendars and pushes shifts if configured.
 */
export function useTriggerCalendarSync() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => api.calendar.triggerSync(),
    onSuccess: () => {
      // The backend already set syncState=Syncing before returning.
      // Optimistically update the cache so polling starts immediately
      // without an extra network round trip.
      queryClient.setQueryData<CalendarConnectionDto[]>(
        selectedCampaignKey(['calendar', 'connections']),
        (old) => old?.map(c => ({ ...c, syncState: CalendarSyncState.Syncing })),
      );
    },
  });
}

// ============================================================================
// Conflicts
// ============================================================================

// ============================================================================
// Cross-Campaign Visibility
// ============================================================================

/** The user's other campaigns and how each one's events show on this calendar. */
export const useCampaignVisibility = defineQuery({
  key: () => ['calendar', 'campaign-visibility'],
  reads: 'GET /api/calendar/campaign-visibility',
  staleTime: 60 * 1000,
  fetch: (_: undefined, { campaignId }) => {
    requireSelectedCampaign(campaignId);
    return api.calendar.getCampaignVisibility();
  },
});

/** Update how a campaign's events show; its write set refreshes the visibility and events reads. */
export function useUpdateCampaignVisibility() {
  return useMutation({
    mutationFn: ({
      campaignId,
      visibility,
    }: {
      campaignId: string;
      visibility: CampaignCalendarVisibility;
    }) => api.calendar.updateCampaignVisibility(campaignId, { visibility }),
  });
}
